import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowRight, Loader2, Mail, Lock, User, Briefcase } from 'lucide-react';

export function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'creative' | 'buyer'>('creative');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      setLoading(false);
      return;
    }

    try {
      const response = await register(name, email, password, role);

      if (response?.requiresApproval) {
        setSuccess(response.message || 'Registration submitted successfully. Your account is pending admin approval.');
        setName('');
        setEmail('');
        setPassword('');
        setRole('creative');
      } else {
        navigate('/');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="font-inter relative min-h-screen w-full overflow-hidden bg-black flex items-center justify-center px-4 py-12">
      {/* Background video — same locally-bundled asset as the hero. */}
      <video
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        controls={false}
        poster="/hero-poster.jpg"
        className="absolute inset-0 w-full h-full object-cover z-0"
      >
        <source src="/hero.webm" type="video/webm" />
        <source src="/hero.mp4" type="video/mp4" />
      </video>

      {/* Dark gradient overlay */}
      <div className="absolute inset-0 z-10 bg-gradient-to-b from-black/65 via-black/45 to-black/70" />

      {/* Register card */}
      <div className="relative z-20 w-full max-w-md">
        <Link to="/" className="flex justify-center mb-8">
          <img
            src="/logo.png"
            alt="Orbit by Pear Media"
            className="h-12 w-auto object-contain drop-shadow-lg"
          />
        </Link>

        <div className="bg-white/[0.06] backdrop-blur-xl border border-white/15 rounded-2xl shadow-2xl p-8">
          <div className="mb-7">
            <h1 className="text-2xl font-bold text-white tracking-tight">Create your account</h1>
            <p className="text-sm text-white/60 mt-1.5">Get started with Orbit</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="px-3 py-2.5 rounded-lg bg-red-500/15 border border-red-500/30 text-red-200 text-sm">
                {error}
              </div>
            )}

            {success && (
              <div className="px-3 py-3 rounded-lg bg-emerald-500/15 border border-emerald-500/30">
                <p className="text-sm text-emerald-100 font-medium">{success}</p>
                <p className="text-xs text-emerald-200/70 mt-2 leading-relaxed">
                  You will be notified once your account is approved. Check back later or contact your administrator.
                </p>
              </div>
            )}

            <FieldShell label="Full name" Icon={User}>
              <input
                id="name"
                type="text"
                placeholder="Jane Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                disabled={!!success}
                autoComplete="name"
                className="w-full pl-10 pr-3 py-2.5 rounded-lg bg-black/30 border border-white/15 text-white placeholder-white/30 focus:outline-none focus:border-orange-400 focus:bg-black/40 transition-colors disabled:opacity-50"
              />
            </FieldShell>

            <FieldShell label="Email" Icon={Mail} hint="Use your official company email address">
              <input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={!!success}
                autoComplete="email"
                className="w-full pl-10 pr-3 py-2.5 rounded-lg bg-black/30 border border-white/15 text-white placeholder-white/30 focus:outline-none focus:border-orange-400 focus:bg-black/40 transition-colors disabled:opacity-50"
              />
            </FieldShell>

            <FieldShell label="Password" Icon={Lock} hint="At least 8 characters">
              <input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                disabled={!!success}
                autoComplete="new-password"
                className="w-full pl-10 pr-3 py-2.5 rounded-lg bg-black/30 border border-white/15 text-white placeholder-white/30 focus:outline-none focus:border-orange-400 focus:bg-black/40 transition-colors disabled:opacity-50"
              />
            </FieldShell>

            <FieldShell label="Role" Icon={Briefcase}>
              <select
                id="role"
                value={role}
                onChange={(e) => setRole(e.target.value as 'creative' | 'buyer')}
                disabled={!!success}
                className="w-full pl-10 pr-3 py-2.5 rounded-lg bg-black/30 border border-white/15 text-white focus:outline-none focus:border-orange-400 focus:bg-black/40 transition-colors appearance-none disabled:opacity-50"
              >
                <option value="creative" className="bg-gray-900">Creative (Upload &amp; Manage Assets)</option>
                <option value="buyer" className="bg-gray-900">Buyer (Use Assets in Campaigns)</option>
              </select>
            </FieldShell>

            <button
              type="submit"
              disabled={loading || !!success}
              className="w-full mt-2 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/50 disabled:cursor-not-allowed text-white font-semibold shadow-lg shadow-orange-500/30 transition-all"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Submitting…
                </>
              ) : success ? (
                'Registration submitted'
              ) : (
                <>
                  Create account
                  <ArrowRight size={16} />
                </>
              )}
            </button>

            <p className="text-center text-sm text-white/60 pt-2">
              Already have an account?{' '}
              <Link to="/login" className="text-orange-400 hover:text-orange-300 font-medium transition-colors">
                Sign in
              </Link>
            </p>
          </form>
        </div>

        <p className="mt-6 text-center text-xs text-white/40">
          © 2026 Pear Media LLC · Orbit
        </p>
      </div>
    </div>
  );
}

/** Shared label + leading-icon wrapper used by every field on this form. */
function FieldShell({
  label,
  Icon,
  hint,
  children,
}: {
  label: string;
  Icon: React.ComponentType<{ size?: number; className?: string }>;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-white/70 uppercase tracking-wider">
        {label}
      </label>
      <div className="relative">
        <Icon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none z-10" />
        {children}
      </div>
      {hint && <p className="text-[11px] text-white/40">{hint}</p>}
    </div>
  );
}
